import { useEffect, useRef } from "react";

/**
 * HeroParticles — the landing hero's ambient dust field.
 *
 * Echoes the DustMark identity: small drifting motes (mostly faint white,
 * a few lime) with hairline constellation links between near neighbours.
 * Pure decoration behind the hero content:
 *
 * - client-only; everything touches window/canvas inside useEffect, so the
 *   vite-react-ssg prerender renders just an empty <canvas>
 * - prefers-reduced-motion → one static frame, no animation loop
 * - rAF pauses while the hero is offscreen (IntersectionObserver) and while
 *   the tab is hidden (visibilitychange)
 * - device pixel ratio capped at 2; render throttled to ~30fps
 * - pointer-events: none; a CSS mask fades the field out toward the bottom
 *   so it never competes with the text or the section border below
 */

type Mote = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  /** base alpha; each mote also breathes ±40% of this */
  a: number;
  /** phase offset for the alpha breathing */
  phase: number;
  lime: boolean;
};

const LIME = "199, 247, 81"; // accent-lime #C7F751
const WHITE = "245, 245, 247"; // text-primary #F5F5F7
const LINK_DIST = 110;
const FRAME_MS = 1000 / 30;

function buildMotes(w: number, h: number, seeded: boolean): Mote[] {
  // Density tuned visually: ~1 mote per 16k px², capped so very wide
  // viewports don't turn into static noise.
   const count = Math.min(99, Math.round((w * h) / 14545));
  const motes: Mote[] = [];
  for (let i = 0; i < count; i++) {
    motes.push({
      x: Math.random() * w,
      y: Math.random() * h,
      // Slow drift with a slight rightward bias — dust streaming through
      // the warp, ~6-14px per second.
      vx: (Math.random() * 8 + 4) / 1000,
      vy: ((Math.random() - 0.5) * 10) / 1000,
      r: Math.random() < 0.18 ? 1.76 + Math.random() * 0.88 : 0.77 + Math.random() * 0.77,
      a: 0.126 + Math.random() * 0.315,
      phase: Math.random() * Math.PI * 2,
      lime: seeded ? i % 7 === 0 : Math.random() < 0.14,
    });
  }
  return motes;
}

export function HeroParticles() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const reduceMq = window.matchMedia("(prefers-reduced-motion: reduce)");
    let motes: Mote[] = [];
    let w = 0;
    let h = 0;
    let raf = 0;
    let running = false;
    let inView = true;
    let lastFrame = 0;
    let lastTick = 0;

    function size() {
      if (!canvas || !ctx) return;
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      motes = buildMotes(w, h, reduceMq.matches);
    }

    function draw(now: number) {
      if (!ctx) return;
      ctx.clearRect(0, 0, w, h);

      // links first, under the motes
      for (let i = 0; i < motes.length; i++) {
        const m = motes[i]!;
        for (let j = i + 1; j < motes.length; j++) {
          const n = motes[j]!;
          const dx = m.x - n.x;
          const dy = m.y - n.y;
          const d2 = dx * dx + dy * dy;
          if (d2 > LINK_DIST * LINK_DIST) continue;
          const t = 1 - Math.sqrt(d2) / LINK_DIST;
          ctx.strokeStyle = `rgba(${m.lime && n.lime ? LIME : WHITE}, ${(t * 0.105).toFixed(3)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(m.x, m.y);
          ctx.lineTo(n.x, n.y);
          ctx.stroke();
        }
      }

      for (const m of motes) {
        const breathe = 1 + 0.4 * Math.sin(now / 2400 + m.phase);
        ctx.fillStyle = `rgba(${m.lime ? LIME : WHITE}, ${(m.a * breathe).toFixed(3)})`;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    function step(dt: number) {
      for (const m of motes) {
        m.x += m.vx * dt;
        m.y += m.vy * dt;
        // wrap with a small margin so motes fade in from outside the edges
        if (m.x > w + 8) m.x = -8;
        if (m.x < -8) m.x = w + 8;
        if (m.y > h + 8) m.y = -8;
        if (m.y < -8) m.y = h + 8;
      }
    }

    function loop(now: number) {
      if (!running) return;
      raf = requestAnimationFrame(loop);
      if (now - lastFrame < FRAME_MS) return;
      step(Math.min(now - lastTick, 100));
      lastTick = now;
      lastFrame = now;
      draw(now);
    }

    function start() {
      if (running || reduceMq.matches || !inView || document.hidden) return;
      running = true;
      lastFrame = 0;
      lastTick = performance.now();
      raf = requestAnimationFrame(loop);
    }

    function stop() {
      running = false;
      cancelAnimationFrame(raf);
    }

    function syncMotion() {
      if (reduceMq.matches) {
        stop();
        draw(0); // static constellation, no motion
      } else {
        start();
      }
    }

    size();
    syncMotion();

    const ro = new ResizeObserver(() => {
      size();
      if (!running) draw(0);
    });
    ro.observe(canvas);

    const io = new IntersectionObserver(([entry]) => {
      inView = entry?.isIntersecting ?? true;
      if (inView) start();
      else stop();
    });
    io.observe(canvas);

    const onVisibility = () => {
      if (document.hidden) stop();
      else start();
    };
    document.addEventListener("visibilitychange", onVisibility);
    reduceMq.addEventListener("change", syncMotion);

    return () => {
      stop();
      ro.disconnect();
      io.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
      reduceMq.removeEventListener("change", syncMotion);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 h-full w-full"
      style={{
        // Fade the field out toward the bottom edge so it never sits under
        // the fine print or fights the section border.
        maskImage:
          "linear-gradient(180deg, rgba(0,0,0,.9) 0%, rgba(0,0,0,1) 30%, rgba(0,0,0,.7) 70%, transparent 100%)",
        WebkitMaskImage:
          "linear-gradient(180deg, rgba(0,0,0,.9) 0%, rgba(0,0,0,1) 30%, rgba(0,0,0,.7) 70%, transparent 100%)",
      }}
    />
  );
}
